import { useState, useMemo } from 'react';
import { X, Search, CheckCircle2, FlaskConical, Cpu } from 'lucide-react';
import { cn } from '@/utils/cn';
import { Tooltip } from '@/components/ui/tooltip';
import { GAMEPLAY_CATEGORIES, type GameplayVariant } from '@/data/gameplay-prompts';

// ─── 玩法详细信息（hover 弹窗展示） ─────────────────────
interface GameplayDetail {
  mode?: string;
  mechanic: string;
  features: string[];
  winLose: string;
  theme: string;
}

const GAMEPLAY_DETAILS: Record<string, GameplayDetail> = {
  // ── 合成系列 ──
  'merge': {
    mode: '拖拽合成（drag）',
    mechanic: '4×4棋盘铺满，拖拽相邻同值水果碰撞合成升级',
    features: ['拖拽合成', '链式合成', '图鉴收集', '最高分'],
    winLose: '胜利：合成到目标等级 | 失败：棋盘堆满',
    theme: '水果（可爱卡通）',
  },
  'merge-collect': {
    mode: '拖拽模式（drag）— 元素不移动',
    mechanic: '手动拖拽相同猫咪合并升级，合成后其他元素保持原位，同时收集图鉴',
    features: ['链式合成', '三合一', '最高分'],
    winLose: '胜利：收集全部6种猫咪图鉴 | 失败：猫咪堆满棋盘',
    theme: '猫咪（温暖治愈）',
  },
  'merge-hongbao': {
    mode: '挂机模式（auto）— 自动合成',
    mechanic: '系统每2秒自动寻找最优合并对执行合成，玩家观赏并领取红包奖励',
    features: ['挂机自动', '链式合成', '红包系统', '金币飞入', '多关卡', '最高分'],
    winLose: '胜利：合成大锦鲤 | 失败：红包堆满溢出',
    theme: '红包（喜庆国潮）',
  },
  'eliminate-merge-earn': {
    mode: '限时拖拽合成（drag）',
    mechanic: '4×4棋盘铺满矿石，拖拽相邻同值矿石合成升级，限时45s + 链式combo + 三合一奖励',
    features: ['拖拽合成', '链式combo', '三合一', '限时倒计时', '红包系统'],
    winLose: '胜利：合成钻石矿 | 失败：棋盘堆满或倒计时归零',
    theme: '矿石（冒险卡通）',
  },
  'merge-jelly-glass': {
    mode: '果冻拖拽合成（jelly_glass）— 仅视觉',
    mechanic: '拖拽相邻同值元素合成升级，机制同水果拖拽合成，3D果冻玻璃半透明折射光效',
    features: ['拖拽合成', '3D质感', '折射光效', '链式合成'],
    winLose: '胜利：合成最高级元素 | 失败：棋盘堆满',
    theme: '果冻玻璃（清新黄绿）',
  },
  'merge-2048': {
    mode: '2048滑动（2048）— 数字合并',
    mechanic: '上下左右滑动整行数字方块，同值碰撞合并翻倍，目标达成2048通关',
    features: ['滑动合并', '数字翻倍', '策略规划', '最高分'],
    winLose: '胜利：合成2048方块 | 失败：无法移动',
    theme: '数字方块（极简风格）',
  },
  'merge-suika': {
    mode: '物理投放（suika）— 大西瓜',
    mechanic: '从顶部投放圆形水果，物理引擎驱动碰撞堆叠，相同水果接触合成升级为大西瓜',
    features: ['物理引擎', '碰撞合成', '投放策略', '警戒线'],
    winLose: '胜利：合成大西瓜 | 失败：堆满超过警戒线',
    theme: '水果物理（可爱卡通）',
  },
  'merge-drop': {
    mode: '方块下落（drop）— 自动合并',
    mechanic: '顶部投放方块自由下落堆叠，同等级方块自动碰撞合并升级',
    features: ['下落堆叠', '自动合并', '投放策略', '警戒线'],
    winLose: '胜利：合成最高级方块 | 失败：堆满溢出',
    theme: '方块下落（简约风格）',
  },
  'merge-chain': {
    mode: '连锁合成（chain）— combo递增',
    mechanic: '拖拽相邻同值元素合成，触发连锁反应 Combo 倍率递增',
    features: ['链式反应', 'combo倍率', '拖拽合成', '爽快感'],
    winLose: '胜利：达到目标分数 | 失败：棋盘堆满',
    theme: '链式水果（爽快卡通）',
  },
  'merge-merge3': {
    mode: '三合一合成（merge3）— 三同即合',
    mechanic: '三个相同元素同时存在时自动触发三合一合成，产生高级元素并获得奖励',
    features: ['三合一奖励', '链式合成', '自动触发', '最高分'],
    winLose: '胜利：合成终极元素 | 失败：棋盘堆满',
    theme: '元素（魔法卡通）',
  },
  'merge-multi-level': {
    mode: '多级合成（multi_level）— 逐级递进',
    mechanic: '拖拽合成逐级递进，每关棋盘扩张，目标不断升级，难度逐步提升',
    features: ['逐级递进', '棋盘扩张', '多关卡', '链式合成'],
    winLose: '胜利：完成所有关卡目标 | 失败：棋盘堆满',
    theme: '城堡（奇幻卡通）',
  },
  'merge-army': {
    mode: '军团合成（army）— 合成御敌',
    mechanic: '拖拽合成升级士兵等级，组建军团抵御敌军波次进攻，策略与合成结合',
    features: ['士兵升级', '波次防御', '策略布阵', '链式合成'],
    winLose: '胜利：击退所有敌军波次 | 失败：基地被攻破',
    theme: '军团（战争卡通）',
  },
  // ── 跑酷系列 ──
  'runner': {
    mode: '三车道跑酷（左右换道）',
    mechanic: '角色自动向前奔跑，左右滑动切换三条车道躲避障碍并收集金币',
    features: ['三车道', '左右换道', '金币收集', '生命系统'],
    winLose: '胜利：到达终点或达目标分数 | 失败：碰撞耗尽生命',
    theme: '森林冒险（明亮卡通）',
  },
  'runner-dodge': {
    mode: '单道躲避跑酷（上下滑动）',
    mechanic: '单条赛道自动前进，上下滑动躲避障碍，碰撞即失败',
    features: ['上下滑动', '单赛道', '加速冲刺', '密度递增'],
    winLose: '胜利：坚持到时间结束按分数结算 | 失败：碰撞障碍物',
    theme: '赛博朋克城市（霓虹风格）',
  },
  'runner-hongbao': {
    mode: '红包跑酷（hongbao）— 边跑边赚',
    mechanic: '跑酷过程中收集红包和金币，通关后触发红包奖励弹窗，玩法与跑酷结合网赚',
    features: ['红包收集', '金币飞入', '跳跃', '滑行', '红包进度条'],
    winLose: '胜利：到达终点+集齐红包 | 失败：撞到障碍',
    theme: '红包跑酷（喜庆卡通）',
  },
  'runner-tunnel': {
    mode: '隧道跑酷（tunnel）— 同左右跑酷，实验中',
    mechanic: '操作同三车道左右跑酷，隧道场景视野受限考验反应',
    features: ['隧道场景', '视野受限', '左右换道', '金币收集'],
    winLose: '胜利：到达终点 | 失败：撞到隧道壁',
    theme: '地下隧道（暗黑霓虹）',
  },
  'runner-waterslide': {
    mode: '滑梯跑酷（waterslide）— 同左右跑酷，实验中',
    mechanic: '操作同三车道左右跑酷，水上乐园滑梯场景收集金币',
    features: ['水上场景', '左右换道', '金币收集', '障碍物'],
    winLose: '胜利：到达终点 | 失败：掉出滑道',
    theme: '水上乐园（清凉蓝色）',
  },
  'runner-space': {
    mode: '太空跑酷（space）— 同左右跑酷，实验中',
    mechanic: '操作同三车道左右跑酷，太空场景闪避陨石障碍',
    features: ['太空场景', '左右换道', '陨石障碍', '金币收集'],
    winLose: '胜利：到达空间站 | 失败：撞上陨石',
    theme: '太空宇宙（科幻风格）',
  },
  'runner-endless': {
    mode: '无尽跑酷（endless）— 无限奔跑',
    mechanic: '无尽模式自动奔跑，速度逐渐加快，躲避随机生成的障碍，挑战最高分',
    features: ['无尽模式', '速度递增', '随机障碍', '最高分', '跳跃', '滑行'],
    winLose: '胜利：挑战最高分 | 失败：撞到障碍物',
    theme: '无尽赛道（霓虹风格）',
  },
  // ── 消除系列 ──
  'match3': {
    mode: '三消模式',
    mechanic: '交换相邻宝石，三个或以上同色连成一线即可消除，上方宝石下落补位',
    features: ['连消combo', '特殊宝石', '目标分数'],
    winLose: '胜利：达到目标分数 | 失败：步数用尽',
    theme: '宝石（炫彩风格）',
  },
  'match3-jelly-glass': {
    mode: '果冻交换三消（jelly_glass）— 仅视觉',
    mechanic: '交换相邻果冻玻璃方块形成3+同色连线消除，机制同宝石三消',
    features: ['3D质感', '折射光效', '连消combo', '特殊宝石'],
    winLose: '胜利：达到目标分数 | 失败：步数用尽',
    theme: '果冻玻璃（清新黄绿）',
  },
  'eliminate-match3': {
    mode: '交换三消（swap）— 糖果主题',
    mechanic: '交换相邻糖果形成3+同色连线消除，机制同宝石三消',
    features: ['交换消除', '连消combo', '特殊道具'],
    winLose: '胜利：达到目标分数 | 失败：步数用尽',
    theme: '糖果（多彩风格）',
  },
  'eliminate-link': {
    mode: '滑动连线消除',
    mechanic: '手指滑动连接相邻同色元素形成路径，至少2颗即消除',
    features: ['路径消除', '连消combo', '限时挑战'],
    winLose: '胜利：达到目标分数 | 失败：步数用尽',
    theme: '星空连线（梦幻风格）',
  },
  'eliminate-tap': {
    mode: '点击区域消除',
    mechanic: '点击一整片相连同色区域消除（至少2格）',
    features: ['区域消除', '连消combo'],
    winLose: '胜利：达到目标分数 | 失败：步数用尽',
    theme: '水果色块（简约风格）',
  },
  'eliminate-mahjong': {
    mode: '麻将配对',
    mechanic: '点击两张相同的未被遮挡的牌消除',
    features: ['配对消除', '限时挑战'],
    winLose: '胜利：消除所有牌 | 失败：无可消除配对或时间耗尽',
    theme: '麻将（传统中国风）',
  },
  'eliminate-mahjong-scatter': {
    mode: '麻将消除',
    mechanic: '点击未被遮挡的牌飞入收集槽，碰（3张相同）或杠（4张相同）自动消除',
    features: ['收集槽', '碰杠消除', '限时挑战', '连消combo'],
    winLose: '胜利：清空全部牌 | 失败：槽位满或时间到',
    theme: '麻将（传统中国风）',
  },
  'eliminate-bubble': {
    mode: '瞄准发射（bubble）— 泡泡射击，实验中',
    mechanic: '从底部瞄准发射泡泡到六角网格，3+同色相连即消除，悬挂泡泡自动掉落',
    features: ['六角网格', '瞄准发射', '悬挂掉落', '颜色匹配'],
    winLose: '胜利：清除所有泡泡 | 失败：泡泡触底线',
    theme: '泡泡（清新可爱）',
  },
  'eliminate-slider': {
    mode: '整行列滑动（slider）— 滑动凑对，实验中',
    mechanic: '上下左右滑动整行或整列方块，滑动后形成3+同色连线即消除',
    features: ['行列滑动', '连线消除', '特殊道具', '连消combo'],
    winLose: '胜利：达到目标分数 | 失败：步数用尽',
    theme: '滑块（简约现代）',
  },
  // ── 放置经营系列 ──
  'idle': {
    mode: '点击农场放置',
    mechanic: '点击赚取金币购买建筑，自动收益持续增长，可升级提高产量',
    features: ['点击收益', '建筑升级', '自动增长', '离线收益'],
    winLose: '胜利：达到目标金币 | 失败：无失败条件',
    theme: '农场经营（田园卡通）',
  },
  'manage-garden': {
    mode: '点击种花收获（garden）',
    mechanic: '点击浇水加速生长，自动采收花朵，升级工具提高采收速度',
    features: ['点击浇水', '自动采收', '植物升级链', '金币飞入'],
    winLose: '胜利：建成花园温室并完成采收目标 | 失败：无失败条件',
    theme: '花草花园（清新治愈）',
  },
  'manage-tycoon': {
    mode: '点击建造城市（tycoon）',
    mechanic: '点击建造升级建筑，自动收益扩张商业区，雇佣经理自动收银',
    features: ['建筑升级链', '经理雇佣', '点击建造', '离线收益'],
    winLose: '胜利：建成摩天大楼 | 失败：无失败条件（休闲）',
    theme: '城市商业（现代简约）',
  },
  'manage-kitchen': {
    mode: '点击餐厅上菜（kitchen）',
    mechanic: '点击食材烹饪菜品，及时上菜赚取小费，升级厨具提高效率',
    features: ['订单系统', '点击烹饪', '厨具升级', '菜品解锁'],
    winLose: '胜利：达成营业额目标 | 失败：顾客流失过多',
    theme: '餐厅厨房（美食卡通）',
  },
  'manage-hongbao': {
    mode: '红包经营（hongbao）— 经营+红包',
    mechanic: '经营店铺同时收集红包奖励，通关后触发红包领取弹窗，网赚叠加玩法',
    features: ['自动收益', '红包奖励', '建筑升级', '金币飞入', '红包进度条'],
    winLose: '胜利：达成经营目标+集齐红包 | 失败：无失败条件',
    theme: '红包经营（喜庆卡通）',
  },
  // ── 解谜系列 ──
  'puzzle': {
    mode: '解谜',
    mechanic: '拖拽碎片拼合完整图案，逐步还原图片',
    features: ['碎片拖拽', '难度递增', '提示功能'],
    winLose: '胜利：完成拼图 | 失败：时间用尽',
    theme: '拼图（写实风格）',
  },
  'puzzle-draw': {
    mode: '画画解谜',
    mechanic: '在画布上绘制路径或图形，引导角色到达目标',
    features: ['手绘路径', '物理模拟', '多关卡'],
    winLose: '胜利：到达终点 | 失败：掉出画布',
    theme: '手绘（简约线条）',
  },
  'puzzle-liquid': {
    mode: '倾倒液体排序（liquid），实验中',
    mechanic: '倾倒试管中液体层使每支试管只含一种颜色（整层移动，非单球）',
    features: ['试管倾倒', '液体分层', '逻辑推理', '步数限制'],
    winLose: '胜利：每管单色 | 失败：无可用倾倒操作',
    theme: '液体试管（简约风格）',
  },
  'puzzle-pin': {
    mode: '拔插销（pin）— 顺序解锁，实验中',
    mechanic: '按正确顺序拔出插销，解锁机关引导小球或液体到达目标位置',
    features: ['插销顺序', '物理模拟', '连锁机关', '多关卡'],
    winLose: '胜利：球/液体到达目标 | 失败：掉出轨道或错误顺序',
    theme: '机关解谜（精密风格）',
  },
  'puzzle-screw': {
    mode: '拧螺丝（screw）— 拆卸板材，实验中',
    mechanic: '按正确顺序拧开螺丝，逐层拆卸叠放的板材，注意板材掉落物理',
    features: ['螺丝顺序', '板材层叠', '物理模拟', '空间推理'],
    winLose: '胜利：拆完所有板材 | 失败：板材掉落砸到目标',
    theme: '工具维修（工业简约）',
  },
  // ── 纸牌系列 ──
  'card': {
    mode: '点击扑克对战',
    mechanic: '与AI对手进行纸牌对决，出牌比大小或凑组合',
    features: ['AI对战', '牌型组合', '策略出牌'],
    winLose: '胜利：赢光对手筹码 | 失败：筹码耗尽',
    theme: '扑克牌（经典风格）',
  },
  'card-poker': {
    mode: '三人斗地主',
    mechanic: '三人斗地主经典规则，与AI对手比牌型出牌',
    features: ['AI对战', '叫牌抢地主', '牌型比较', '策略出牌'],
    winLose: '胜利：率先出完手牌 | 失败：对手先出完',
    theme: '斗地主（经典风格）',
  },
  'card-solitaire': {
    mode: '纸牌接龙',
    mechanic: '将牌按花色和数字顺序排列到基础堆，从A到K依次完成',
    features: ['拖拽排列', '翻牌堆', '自动完成'],
    winLose: '胜利：四堆全部完成 | 失败：无可移动牌',
    theme: '经典纸牌（简约风格）',
  },
  'card-hongbao': {
    mode: '红包纸牌（hongbao）— 对战+红包',
    mechanic: '纸牌对战获胜后领取红包奖励，网赚叠加玩法，每关通关触发红包弹窗',
    features: ['AI对战', '红包奖励', '牌型组合', '金币飞入', '红包进度条'],
    winLose: '胜利：赢光对手筹码+集齐红包 | 失败：筹码耗尽',
    theme: '红包扑克（喜庆风格）',
  },
  // ── 挪车系列 ──
  'parking': {
    mode: '华容道挪车（classic）',
    mechanic: '拖拽车辆移动，为目标车腾出通道，目标车驶出出口即胜利',
    features: ['拖拽移动', '步数记录', '最佳记录', '惯性滑动'],
    winLose: '胜利：目标车驶出出口 | 失败：步数用尽或时间耗尽',
    theme: '城市停车场（卡通俯视）',
  },
  'puzzle-parking': {
    mode: '停车解谜（obstacle）',
    mechanic: '场上有立柱/墙壁等不可移动障碍，需绕过或利用机关腾出通道',
    features: ['障碍物', '步数限制', '最佳记录'],
    winLose: '胜利：目标车驶出出口 | 失败：步数用尽',
    theme: '地下停车场（写实俯视）',
  },
  'parking-hongbao': {
    mode: '挪车红包（classic+红包）',
    mechanic: '拖拽移动车辆，目标车驶出即胜利，每关通关触发红包奖励',
    features: ['红包奖励', '步数记录', '最佳记录', '计时器', '红包进度条'],
    winLose: '胜利：目标车驶出出口 | 失败：步数用尽',
    theme: '红包停车场（喜庆卡通）',
  },
  'parking-tap-away': {
    mode: '点击开走（tap_away）',
    mechanic: '点击车辆自动沿朝向前进直到碰撞或驶出，所有车驶出即胜利',
    features: ['点击操作', '自动行驶', '多出口', '超休闲'],
    winLose: '胜利：所有车辆驶出 | 失败：时间耗尽',
    theme: '交通堵塞疏散（卡通俯视）',
  },
  'parking-bus-out': {
    mode: '颜色接人（bus_out）',
    mechanic: '点击与公交同色的乘客上车，满员后公交自动驶出',
    features: ['颜色匹配', '乘客接载', '多出口', '配色消除'],
    winLose: '胜利：所有乘客被接走 | 失败：时间耗尽',
    theme: '公车站点（卡通都市）',
  },
  'parking-all-escape': {
    mode: '全车逃脱（all_escape）',
    mechanic: '四周多出口数量不固定，拖拽腾出通道使全部车辆驶出',
    features: ['多出口', '全局规划', '高难度', 'Combo连击'],
    winLose: '胜利：所有车辆驶出 | 失败：步数用尽或时间耗尽',
    theme: '停车场大疏散（卡通俯视）',
  },
  'parking-target-escape': {
    mode: '救护车救援（target_escape）',
    mechanic: '拖拽障碍车为白色救护车清出通道，救护车必须驶出出口（无火势机制）',
    features: ['目标车救援', '紧迫感', '救护车', '时间压力'],
    winLose: '胜利：救护车驶出出口 | 失败：通道被堵或时间耗尽',
    theme: '急救通道（写实俯视）',
  },
  'parking-color-match': {
    mode: '停车匹配（parking_match）',
    mechanic: '拖拽车辆停入对应颜色的车位，全部匹配即胜利',
    features: ['颜色匹配', '无出口', '停车位标线', '步数记录'],
    winLose: '胜利：所有车辆停入对应车位 | 失败：步数用尽',
    theme: '彩色停车场（卡通俯视）',
  },
  'parking-color-sort': {
    mode: '颜色排序（color_sort）',
    mechanic: '拖拽车辆使同色车辆相邻聚集，全部排好即胜利',
    features: ['颜色排序', '无出口', '分拣区', '步数记录'],
    winLose: '胜利：所有颜色同色聚集 | 失败：步数用尽',
    theme: '车辆分拣场（卡通俯视）',
  },
  'parking-combo': {
    mode: '连击开走（combo）',
    mechanic: '点击车辆自动驶出，连续驶出获Combo倍率奖励',
    features: ['Combo连击', '点击操作', '自动行驶', '多出口', '爽感强'],
    winLose: '胜利：所有车辆驶出 | 失败：时间耗尽或Combo中断',
    theme: '连锁停车场（霓虹卡通）',
  },
  'parking-dual-exit': {
    mode: '双口通行（dual_exit）',
    mechanic: '拖拽车辆，两个出口分别在左右，玩家决定每辆车走哪个出口',
    features: ['双出口', '策略选择', '全局规划', 'Combo连击'],
    winLose: '胜利：所有车辆驶出 | 失败：步数用尽或时间耗尽',
    theme: '智慧停车场（写实俯视）',
  },
  'parking-triple-exit': {
    mode: '三出口疏散（triple_exit）',
    mechanic: '拖拽车辆，三个出口分布在不同方向，横纵混合车辆需策略选择最优出口',
    features: ['三出口', '横纵混合', '高级策略', '全局规划', 'Combo连击'],
    winLose: '胜利：所有车辆驶出 | 失败：步数用尽或时间耗尽',
    theme: '大型停车场（写实俯视）',
  },
  'parking-quad-exit': {
    mode: '四出口全向（quad_exit）',
    mechanic: '拖拽车辆，四个出口分布在上下左右，全向疏散需要极强的全局规划能力',
    features: ['四出口', '全向疏散', '极高难度', '全局规划'],
    winLose: '胜利：所有车辆驶出 | 失败：步数用尽或时间耗尽',
    theme: '枢纽停车场（现代俯视）',
  },
  'parking-rescue-princess': {
    mode: '解救公主（rescue_princess）',
    mechanic: '多出口停车场，粉色公主车为指定目标，拖拽规划路线使全车驶出并救出角色',
    features: ['多出口', '粉色目标车', '路径规划', '剧情驱动'],
    winLose: '胜利：公主车及全部车辆驶出 | 失败：步数用尽',
    theme: '童话停车场（奇幻卡通）',
  },
  'parking-fire-spread': {
    mode: '火势封路（fire_spread）',
    mechanic: '火焰定期向四周扩散封路，拖拽车辆在火场中为目标车开辟通道驶出',
    features: ['火焰扩散', '路径封锁', '时间压力', '紧迫感'],
    winLose: '胜利：目标车驶出出口 | 失败：火封死通道',
    theme: '烈焰停车场（写实紧迫）',
  },
  'parking-valet': {
    mode: '代客泊车（valet）',
    mechanic: '接待队列中车辆依次入场，拖拽引导每辆车开入对应的指定车位',
    features: ['队列系统', '车位匹配', '步数记录', '最佳记录'],
    winLose: '胜利：所有车辆停入指定车位 | 失败：步数用尽',
    theme: '酒店泊车（高端卡通）',
  },
  'parking-traffic-light': {
    mode: '信号灯通行（traffic_light）',
    mechanic: '红绿灯交替控制车辆通行权限，切换信号灯指挥车辆驶出，闯红灯违章计数',
    features: ['红绿灯控制', '信号切换', '违章计数', '策略通行'],
    winLose: '胜利：所有车辆合规驶出 | 失败：违章次数超限',
    theme: '十字路口停车场（卡通俯视）',
  },
  'parking-elevator': {
    mode: '立体车库（elevator）',
    mechanic: '多层立体车库，电梯在楼层间升降运输车辆，跨层腾挪驶出目标车',
    features: ['电梯换层', '多层停车', '跨层策略', '步数记录'],
    winLose: '胜利：目标车驶出出口 | 失败：步数用尽',
    theme: '立体车库（现代工业）',
  },
  'parking-meter-fare': {
    mode: '计价计费（meter_fare）',
    mechanic: '每移动一步扣除停车费，余额归零即失败，需用最少步数完成挑战',
    features: ['步数计费', '余额限制', '最少步数', '策略优化'],
    winLose: '胜利：目标车驶出且余额>0 | 失败：余额耗尽',
    theme: '收费停车场（都市卡通）',
  },
  'parking-taxi-dispatch': {
    mode: '出租车派单（taxi_dispatch）',
    mechanic: '订单持续派发，点击对应颜色出租车自动驶出完成派单，连单有额外奖励',
    features: ['点击接单', '订单派发', '颜色匹配', '节奏压力'],
    winLose: '胜利：完成目标订单数 | 失败：超时失败',
    theme: '出租车站（都市夜景）',
  },
  // ── 箭头系列 ──
  'arrow': {
    mode: '点击滑出（基础）',
    mechanic: '点击长条/折线方块，整段沿箭头方向滑出棋盘，清空所有方块即胜利',
    features: ['多格方块', '方向箭头', '整段滑出', '出口飞出'],
    winLose: '胜利：所有方块移出 | 失败：时间耗尽',
    theme: '几何方块（极简扁平）',
  },
  'arrow-classic-cell': {
    mode: '单格滑出（classic_cell）',
    mechanic: '单格方块沿箭头指向滑出棋盘，碰撞阻挡则无法移动',
    features: ['单格滑动', '碰撞阻挡', '纯策略', '多方向'],
    winLose: '胜利：清空所有方块 | 失败：无',
    theme: '方块棋盘（极简扁平）',
  },
  'arrow-time-blitz': {
    mode: '限时闪击（time_blitz）— 倒计时竞速',
    mechanic: '首次点击启动倒计时，限时内移除≥80%箭头即胜利，考验速度与策略',
    features: ['倒计时', '80%目标', '竞速', '首次点击触发'],
    winLose: '胜利：移除≥80%箭头 | 失败：倒计时归零',
    theme: '闪电棋盘（科技感）',
  },
  'arrow-color-match': {
    mode: '颜色配对（color_match）— 同色双击',
    mechanic: '双击两支同色箭头配对同时移出，移动轨迹不得重叠才能成功配对',
    features: ['颜色配对', '双击操作', '轨迹检测', '同时消除'],
    winLose: '胜利：清空所有配对 | 失败：无可用配对',
    theme: '彩虹棋盘（多彩风格）',
  },
  'arrow-one-stroke': {
    mode: '一笔画（one_stroke）— 顺序挑战',
    mechanic: '按路径顺序依次点击箭头移出，点击顺序错误会计数，考验路径规划',
    features: ['顺序点击', '错误计数', '路径规划', '一笔画规则'],
    winLose: '胜利：按序清空 | 失败：错误次数超限',
    theme: '路径棋盘（线条风格）',
  },
  'arrow-chain-combo': {
    mode: '编号连击（chain_combo）— 从小到大',
    mechanic: '箭头带编号，必须从小到大依次点击，连续正确触发Combo倍率加分',
    features: ['编号排序', '连击Combo', '倍率加分', '节奏感'],
    winLose: '胜利：按序清空所有编号箭头 | 失败：点错顺序',
    theme: '数字棋盘（科技风格）',
  },
  'arrow-relay': {
    mode: '接力移出（relay）— 激活连锁',
    mechanic: '只有高亮的激活箭头可点击，移除后自动激活相邻的下一支箭头',
    features: ['激活机制', '连锁触发', '策略选择', '逐步解锁'],
    winLose: '胜利：清空所有箭头 | 失败：无激活箭头可操作',
    theme: '接力棋盘（荧光风格）',
  },
  'arrow-reverse': {
    mode: '反向移动（reverse）— 反向思维',
    mechanic: '箭头实际移动方向与指向完全相反，需要反向思维规划移出路径',
    features: ['反向移动', '思维反转', '策略规划', '难度递增'],
    winLose: '胜利：清空所有箭头 | 失败：无可用移动',
    theme: '反转棋盘（镜像风格）',
  },
  'arrow-domino': {
    mode: '多米诺连锁（domino）— 一键清场',
    mechanic: '点击链头箭头触发连锁反应，后方排列的箭头依次自动移出',
    features: ['连锁反应', '一键清场', '链头识别', '爽快感'],
    winLose: '胜利：清空所有箭头 | 失败：无法触发有效连锁',
    theme: '骨牌棋盘（古典风格）',
  },
  'arrow-fewest-moves': {
    mode: '最少步数（fewest_moves）— 极限优化',
    mechanic: '在目标步数内清空全部箭头，支持撤销悔步，考验最优解推理',
    features: ['步数限制', '撤销悔步', '最优解', '策略推理'],
    winLose: '胜利：目标步数内清空 | 失败：步数耗尽仍有剩余',
    theme: '极简棋盘（黑白风格）',
  },
  'arrow-pixel-drain': {
    mode: '像素轨迹（pixel_drain）— 覆盖填色',
    mechanic: '箭头滑出后留下像素轨迹，覆盖棋盘上所有目标格子即胜利',
    features: ['轨迹覆盖', '目标格子', '路径规划', '像素风格'],
    winLose: '胜利：覆盖全部目标格 | 失败：无法覆盖所有格子',
    theme: '像素棋盘（复古游戏风）',
  },
  'arrow-pvp-lite': {
    mode: '双人对战（pvp_lite）— 镜像对抗',
    mechanic: '上下镜像棋盘双人对战AI，各自操作，先清空自己一侧者获胜',
    features: ['双人PvP', '镜像棋盘', 'AI对手', '竞速对抗'],
    winLose: '胜利：先清空自己一侧 | 失败：对手先清空',
    theme: '对阵棋盘（红蓝对抗）',
  },
  'arrow-push-chain': {
    mode: '连推位移（push_chain）— 推挤连锁',
    mechanic: '点击箭头推挤同排所有箭头，产生一格连锁位移，利用推挤清空棋盘',
    features: ['推挤位移', '连锁反应', '同排推动', '策略推演'],
    winLose: '胜利：清空所有箭头 | 失败：无可用推挤',
    theme: '推挤棋盘（机械风格）',
  },
  'arrow-teleport': {
    mode: '传送门（teleport）— 瞬移穿越',
    mechanic: '箭头踩到传送门时瞬移到配对传送门位置，继续沿原方向移动',
    features: ['传送门', '瞬移穿越', '配对传送', '路径规划'],
    winLose: '胜利：清空所有箭头 | 失败：陷入死循环',
    theme: '传送棋盘（科幻风格）',
  },
  'arrow-ice-slide': {
    mode: '冰面滑行（ice_slide）— 惯性滑动',
    mechanic: '冰面上箭头一旦开始移动就不可停止，只能撞到障碍物或其他箭头才停',
    features: ['冰面惯性', '不可停止', '碰撞停止', '策略规划'],
    winLose: '胜利：清空所有箭头 | 失败：无法全部滑出',
    theme: '冰面棋盘（冰雪风格）',
  },
  'arrow-turn-tile': {
    mode: '转向板（turn_tile）— 改变方向',
    mechanic: '棋盘上放置转向板，方块经过时被迫改变移动方向，利用转向板导航出棋盘',
    features: ['转向板', '方向改变', '路径导航', '策略规划'],
    winLose: '胜利：所有箭头经转向后移出 | 失败：无法全部移出',
    theme: '转向棋盘（迷宫风格）',
  },
  'arrow-multi-head': {
    mode: '双头箭头（multi_head）— 双向消除',
    mechanic: '双头箭头占据两格，当两个方向均畅通时整体消除，需要双向同时满足',
    features: ['双头箭头', '双向消除', '占两格', '条件消除'],
    winLose: '胜利：清空所有箭头 | 失败：无法消除双头箭头',
    theme: '双头棋盘（对称风格）',
  },
  'arrow-split': {
    mode: '分裂箭头（split）— 一分为二',
    mechanic: '点击后箭头分裂为两支，分别沿垂直方向双向滑出，一变二策略清空',
    features: ['分裂机制', '一分为二', '垂直双向', '数量增长'],
    winLose: '胜利：清空所有箭头 | 失败：分裂后无法全部移出',
    theme: '分裂棋盘（分形风格）',
  },
  'arrow-queue': {
    mode: '队列放行（queue）— 按序通过',
    mechanic: '箭头按队列顺序依次放行，每轮只能移动队列首部的箭头，策略安排放行顺序',
    features: ['队列系统', '按序放行', '策略安排', '回合制'],
    winLose: '胜利：清空所有箭头 | 失败：队列堵塞无法继续',
    theme: '队列棋盘（交通风格）',
  },
  // ── 答题系列 ──
  'guess-song': {
    mode: '点歌猜曲',
    mechanic: '听音乐片段，从选项中选择正确的歌曲名称',
    features: ['音乐播放', '选项选择', '倒计时'],
    winLose: '胜利：答对目标题数 | 失败：答错超过限制',
    theme: '音乐竞猜（动感风格）',
  },
  'idiom-fill': {
    mode: '选字填成语',
    mechanic: '成语缺字，从候选字中选出正确的字补齐成语，答对领现金奖励',
    features: ['选字补齐', '现金奖励', '红包进度条', '倒计时'],
    winLose: '胜利：答对目标题数 | 失败：答错超过限制',
    theme: '国风山水书卷（水墨风格）',
  },
  'quiz-image': {
    mode: '看图猜物（image）— 图片提示，实验中',
    mechanic: '展示一张模糊或局部的图片，从多个选项中选择正确答案',
    features: ['图片展示', '模糊提示', '选项选择', '倒计时'],
    winLose: '胜利：答对目标题数 | 失败：答错超过限制',
    theme: '百科图片（多彩风格）',
  },
  'quiz-knowledge': {
    mode: '知识竞答（knowledge）— 多类别选择，实验中',
    mechanic: '涵盖历史/地理/科学/文学等多类别知识选择题，难度逐步递增',
    features: ['多类别题库', '难度递增', '选项选择', '倒计时'],
    winLose: '胜利：答对目标题数 | 失败：答错超过限制',
    theme: '知识竞赛（学术风格）',
  },
  'quiz-word': {
    mode: '文字问答（word）— 成语文字，实验中',
    mechanic: '成语接龙、文字填空、谐音梗等文字类知识题，选择或填字作答',
    features: ['成语接龙', '文字填空', '谐音梗', '倒计时'],
    winLose: '胜利：答对目标题数 | 失败：答错超过限制',
    theme: '国学文字（书法风格）',
  },
  // ── 红包系列（实验中） ──
  'hongbao': {
    mode: '通用红包',
    mechanic: '多种红包互动方式，点击/刮/切等获取红包奖励',
    features: ['红包奖励', '多种互动', '金币飞入'],
    winLose: '胜利：收集目标红包数 | 失败：时间耗尽',
    theme: '红包（喜庆国潮）',
  },
  'hongbao-rain': {
    mode: '红包雨',
    mechanic: '大量红包从天而降，快速点击接取',
    features: ['快速反应', '连击combo', '金币飞入', '限时挑战'],
    winLose: '胜利：接到目标红包数 | 失败：时间耗尽',
    theme: '红包雨（喜庆热闹）',
  },
  'hongbao-scratch': {
    mode: '刮刮卡',
    mechanic: '手指刮开涂层，揭示红包奖励',
    features: ['刮开涂层', '揭示奖励', '多张连刮'],
    winLose: '胜利：刮到大奖 | 失败：刮完未中',
    theme: '刮刮乐（喜庆卡牌）',
  },
  'hongbao-slash': {
    mode: '滑动切割',
    mechanic: '滑动屏幕切割飞来的红包，类似水果忍者',
    features: ['滑动切割', '连击combo', '快速反应', '限时挑战'],
    winLose: '胜利：切到目标红包数 | 失败：漏切过多',
    theme: '切红包（动感爽快）',
  },
  'hongbao-tap': {
    mode: '快速点击',
    mechanic: '快速点击屏幕上出现的红包，越快越多越好',
    features: ['快速点击', '连击combo', '反应速度', '限时挑战'],
    winLose: '胜利：点到目标红包数 | 失败：时间耗尽',
    theme: '点击红包（喜庆活泼）',
  },
  'hongbao-collect': {
    mode: '拖拽收集',
    mechanic: '拖拽收集散落在屏幕上的红包',
    features: ['拖拽收集', '路径规划', '限时挑战'],
    winLose: '胜利：收集全部红包 | 失败：时间耗尽',
    theme: '收集红包（喜庆收集）',
  },
  'hongbao-mining': {
    mode: '挖矿点击',
    mechanic: '点击挖取埋藏在地下的红包，越深越大',
    features: ['挖矿', '深度递增', '随机奖励', '限时挑战'],
    winLose: '胜利：挖到大奖 | 失败：时间耗尽',
    theme: '挖矿红包（冒险挖掘）',
  },
  'hongbao-whack': {
    mode: '打地鼠',
    mechanic: '点击从洞中弹出的红包，类似打地鼠',
    features: ['快速反应', '连击combo', '多洞口', '限时挑战'],
    winLose: '胜利：打到目标红包数 | 失败：时间耗尽',
    theme: '打地鼠红包（喜庆欢乐）',
  },
  'hongbao-zuma': {
    mode: '祖玛射击',
    mechanic: '射出红包消除队列中同色红包，防止到达终点',
    features: ['瞄准射击', '颜色匹配', '连消combo', '路径推进'],
    winLose: '胜利：清空队列 | 失败：队列到达终点',
    theme: '祖玛红包（神秘古典）',
  },
  // ── 抽奖系列（实验中） ──
  'lottery-wheel': {
    mode: '大转盘，实验中',
    mechanic: '点击旋转转盘，停止后指针指向奖品',
    features: ['转盘旋转', '随机奖品', '概率机制'],
    winLose: '胜利：转到大奖 | 失败：转到谢谢参与',
    theme: '大转盘（嘉年华风格）',
  },
  'lottery-scratch': {
    mode: '刮刮乐，实验中',
    mechanic: '刮开涂层揭示图案，集齐指定图案获奖',
    features: ['刮开涂层', '图案匹配', '多张连刮'],
    winLose: '胜利：集齐中奖图案 | 失败：刮完未中',
    theme: '刮刮乐（彩票风格）',
  },
  'lottery-smash': {
    mode: '砸金蛋，实验中',
    mechanic: '点击砸开金蛋，揭示内部奖品',
    features: ['点击砸开', '随机奖品', '碎裂特效', '多蛋选择'],
    winLose: '胜利：砸到大奖 | 失败：砸完未中',
    theme: '砸金蛋（黄金庆典）',
  },
  'lottery-draw': {
    mode: '幸运抽奖，实验中',
    mechanic: '点击抽奖按钮，标准抽奖流程展示奖品',
    features: ['抽奖动画', '奖品展示', '概率机制', '多次抽奖'],
    winLose: '胜利：抽到大奖 | 失败：抽奖次数用尽',
    theme: '幸运抽奖（华丽舞台）',
  },
  // ── 射击系列（实验中） ──
  'shooter-target': {
    mode: '靶场点射，实验中',
    mechanic: '射击特定目标，躲避非目标物体',
    features: ['目标识别', '精准射击', '躲避误伤', '连击combo'],
    winLose: '胜利：击中目标数 | 失败：误伤过多或时间到',
    theme: '射击场（战术风格）',
  },
  'shooter-duck': {
    mode: '飞鸭射击（duck）— 经典打猎，实验中',
    mechanic: '点击命中飞过的鸭子/飞碟，限时内击中目标数达标即胜利',
    features: ['飞行目标', '点击射击', '限时挑战', '连击combo'],
    winLose: '胜利：击中目标数 | 失败：放过太多目标',
    theme: '猎场（自然户外）',
  },
  // ── 找茬系列 ──
  'spot-diff-classic': {
    mode: '双图找茬',
    mechanic: '对比上下两张图片，点击找出所有不同之处',
    features: ['双图对比', '点击标记', '限时挑战', '提示功能', '程序化差异'],
    winLose: '胜利：找到全部差异 | 失败：时间耗尽',
    theme: '找茬（写实风格）',
  },
  'spot-diff-search': {
    mode: '搜索找茬',
    mechanic: '在密集元素中搜索指定目标物品',
    features: ['密集搜索', '目标识别', '限时挑战', '难度递增'],
    winLose: '胜利：找到全部目标 | 失败：时间耗尽',
    theme: '搜索（多彩密集）',
  },
  'spot-diff-hidden': {
    mode: '清单寻宝',
    mechanic: '按右侧清单在复杂场景中找出隐藏物品',
    features: ['侧边清单', '隐藏物品', '限时挑战', '提示功能'],
    winLose: '胜利：找全清单物品 | 失败：时间耗尽',
    theme: '寻宝（魔法森林）',
  },
  'spot-diff-animated': {
    mode: '动态找茬',
    mechanic: '在动态城市场景双图中找出差异',
    features: ['动态元素', '双图对比', '限时挑战', '动画装饰'],
    winLose: '胜利：找到全部差异 | 失败：时间耗尽',
    theme: '城市街景（生动卡通）',
  },
  // ── 塔防系列（实验中） ──
  'tower-defense': {
    mode: '放置塔防，实验中',
    mechanic: '放置防御塔抵御敌人波次进攻，升级塔提升战力',
    features: ['塔放置', '波次进攻', '塔升级', '路径规划', '资源管理'],
    winLose: '胜利：抵御所有波次 | 失败：敌人到达基地',
    theme: '塔防（奇幻战争）',
  },
  'tower-defense-maze': {
    mode: '迷宫塔防（maze）— 自建路径',
    mechanic: '敌人沿迷宫路径行进，玩家在路径沿途建造和升级防御塔拦截',
    features: ['迷宫路径', '塔建造', '路径规划', '波次进攻'],
    winLose: '胜利：消灭所有敌人 | 失败：敌人到达终点',
    theme: '迷宫塔防（策略战争）',
  },
  'tower-defense-endless': {
    mode: '无尽塔防（endless）— 无限波次',
    mechanic: '无限波次敌人进攻，每波难度递增，升级防御塔挑战最高分',
    features: ['无尽波次', '难度递增', '塔升级', '最高分'],
    winLose: '胜利：挑战最高分 | 失败：基地被攻破',
    theme: '无尽塔防（暗黑风格）',
  },
  // ── 休闲系列（实验中） ──
  'misc-ball-sort': {
    mode: '球分类，实验中',
    mechanic: '将不同颜色球倒入试管，使每管同色（整球移动，非液体分层）',
    features: ['试管倒球', '颜色分类', '逻辑推理', '步数限制'],
    winLose: '胜利：所有试管单色 | 失败：步数用尽',
    theme: '球分类（简约风格）',
  },
  'misc-catch': {
    mode: '接物投掷，实验中',
    mechanic: '移动容器接住掉落物品，躲避危险物',
    features: ['移动接物', '躲避危险', '连击combo', '限时挑战'],
    winLose: '胜利：接到目标数量 | 失败：接到危险物或时间到',
    theme: '接物（活泼卡通）',
  },
  'misc-knife': {
    mode: '飞刀插入，实验中',
    mechanic: '点击将飞刀插入旋转的圆盘，不碰到已有的刀',
    features: ['飞刀射击', '旋转目标', '空间避让', '连击combo'],
    winLose: '胜利：插完所有刀 | 失败：刀碰刀',
    theme: '飞刀（精准风格）',
  },
  'misc-decor': {
    mode: '房间装饰，实验中',
    mechanic: '拖拽家具布置房间，按主题搭配完成装饰',
    features: ['家具拖拽', '主题搭配', '自由摆放', '评分系统'],
    winLose: '胜利：达到目标评分 | 失败：无失败条件',
    theme: '装饰（温馨家居）',
  },
  'misc-fishing': {
    mode: '钓鱼，实验中',
    mechanic: '等待鱼上钩后点击收竿，时机决定成败',
    features: ['等待时机', '快速反应', '鱼类收集', '限时挑战'],
    winLose: '胜利：钓到目标鱼 | 失败：鱼逃跑或时间到',
    theme: '钓鱼（自然风景）',
  },
  'misc-multi-ending': {
    mode: '多结局，实验中',
    mechanic: '不同选择触发不同故事分支和结局',
    features: ['分支选择', '多结局', '故事剧情', '角色互动'],
    winLose: '胜利：达成好结局 | 失败：达成坏结局',
    theme: '多结局（剧情风格）',
  },
  // ── 抽卡社交系列（实验中） ──
  'gacha-pull': {
    mode: '抽卡，实验中',
    mechanic: '点击抽卡展示随机角色结果，稀有度不同',
    features: ['随机结果', '稀有度系统', '抽卡动画', '角色收集'],
    winLose: '胜利：抽到稀有角色 | 失败：抽卡次数用尽',
    theme: '抽卡（华丽二次元）',
  },
  'social-swipe': {
    mode: '滑动社交，实验中',
    mechanic: '模拟社交APP左右滑动浏览内容，喜欢/不喜欢',
    features: ['左右滑动', '内容浏览', '喜欢/跳过', '匹配机制'],
    winLose: '胜利：匹配成功 | 失败：无匹配',
    theme: '社交（现代简约）',
  },
  // ── 展示换装系列（实验中） ──
  'showcase-slide': {
    mode: '滑动看图，实验中',
    mechanic: '左右滑动浏览内容页面，展示产品或故事',
    features: ['左右滑动', '页面浏览', '内容展示', '自动播放'],
    winLose: '胜利：浏览完所有页面 | 失败：无失败条件',
    theme: '展示（简约风格）',
  },
  'showcase-interactive': {
    mode: '点击热点详情，实验中',
    mechanic: '点击画面热点展开交互详情或动画',
    features: ['点击交互', '动画触发', '信息展示', '多区域'],
    winLose: '胜利：完成所有交互 | 失败：无失败条件',
    theme: '互动展示（科技风格）',
  },
  'dress-up': {
    mode: '点击服饰换装，实验中',
    mechanic: '选择服饰搭配完成主题穿搭，评分系统',
    features: ['服饰选择', '主题搭配', '自由组合', '评分系统'],
    winLose: '胜利：达到目标评分 | 失败：无失败条件',
    theme: '换装（时尚风格）',
  },
};

function renderGameplayDetail(detail: GameplayDetail) {
  return (
    <div className="space-y-1">
      {detail.mode && (
        <div><span className="text-muted-foreground">玩法模式</span>：{detail.mode}</div>
      )}
      <div><span className="text-muted-foreground">核心机制</span>：{detail.mechanic}</div>
      <div><span className="text-muted-foreground">特殊功能</span>：{detail.features.join(' · ')}</div>
      <div><span className="text-muted-foreground">胜负条件</span>：{detail.winLose}</div>
      <div><span className="text-muted-foreground">主题风格</span>：{detail.theme}</div>
    </div>
  );
}

// ─── 已稳定支持的玩法类型（与后端 STABLE_GAMEPLAY_TYPES 保持一致） ───
// 注意：稳定度与技术实现路径(supportLevel)正交
// — 'blueprint' 类型如 guess-song 可以是 stable（已支持）
// — 'full' 类型如 runner 可以是 experimental（实验中）
const STABLE_GAMEPLAY_KEYS = new Set([
  // 三消系列
  'match3', 'match3-jelly-glass', 'eliminate-match3', 'eliminate-link', 'eliminate-tap', 'eliminate-mahjong', 'eliminate-mahjong-scatter',
  // 挪车系列
  'parking', 'puzzle-parking', 'parking-tap-away', 'parking-bus-out', 'parking-all-escape', 'parking-target-escape', 'parking-color-match', 'parking-color-sort', 'parking-combo',   'parking-dual-exit',
  // 挪车三出口/四出口/解救公主/跑道半圈
  'parking-triple-exit', 'parking-quad-exit', 'parking-rescue-princess', 'parking-lap-exit',
  // 挪车 v2 系列（VARIANT_PRESETS 第二波：火灾救援/代客泊车/信号灯/立体车库/计价计费/出租车派单）
  'parking-fire-spread', 'parking-valet', 'parking-traffic-light', 'parking-elevator', 'parking-meter-fare', 'parking-taxi-dispatch',
  // 箭头系列（含十九态变体：十一态新 + 八态既有）
  'arrow', 'arrow-classic-cell', 'arrow-time-blitz', 'arrow-color-match', 'arrow-one-stroke', 'arrow-chain-combo', 'arrow-relay', 'arrow-reverse', 'arrow-domino', 'arrow-fewest-moves', 'arrow-pixel-drain', 'arrow-pvp-lite', 'arrow-push-chain', 'arrow-teleport', 'arrow-ice-slide', 'arrow-turn-tile', 'arrow-multi-head', 'arrow-split', 'arrow-queue',
  // 合成系列（含十态变体）
  'merge', 'merge-jelly-glass', 'merge-collect', 'merge-2048', 'merge-suika', 'merge-drop', 'merge-chain', 'merge-merge3', 'merge-multi-level', 'merge-army', 'eliminate-merge-earn',
  // 经营系列
  'idle', 'manage-tycoon', 'manage-kitchen', 'manage-garden',
  // 其他
  'guess-song', 'idiom-fill',
  // Cocos 生成系列（按变体 id 独立判定，与同名模板/蓝图入口互不影响）
  'cocos-manage-tycoon', 'cocos-manage-kitchen', 'cocos-manage-garden',
  'cocos-arrow', 'cocos-arrow-time-blitz', 'cocos-arrow-color-match', 'cocos-arrow-one-stroke',
]);

/** 稳定度 key：Cocos 生成变体按 variant.id 判定（复用基础 promptKey 但不牵连模板/蓝图入口），其余按 promptKey */
function stableKeyOf(v: GameplayVariant): string {
  return v.engineBadge === 'cocos' ? v.id : v.promptKey;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /**
   * @param key 玩法 promptKey（如 match3、parking-tap-away），用于查询 GAMEPLAY_PROMPTS
   * @param label 变体展示名
   * @param variant 完整变体对象（含 engineBadge 等元信息，供调用方决定是否切换生成模式）
   * 网赚红包叠加开关已迁移为聊天框上方全局开关（BUG-20260820-WEB-001），不再按卡片传递
   */
  onSelect?: (key: string, label: string, variant?: GameplayVariant) => void;
}

// DO-NOT-REVERT: BUG-20260820-WEB-001 每卡🧧红包按钮已移除，叠加改由 ProjectDetail 聊天框上方全局开关控制，禁止在本组件恢复每卡按钮
export function GameplaySelector({ open, onClose, onSelect }: Props) {
  const [search, setSearch] = useState('');
  const [activeSupportTab, setActiveSupportTab] = useState<'supported' | 'experimental'>('supported');
  const [activeCategory, setActiveCategory] = useState('');

  // 根据一级 Tab 过滤分类（按稳定度而非技术实现路径）
  const filteredCategories = useMemo(() => {
    const isStable = activeSupportTab === 'supported';
    return GAMEPLAY_CATEGORIES
      .map(cat => ({
        ...cat,
        variants: cat.variants.filter(v => STABLE_GAMEPLAY_KEYS.has(stableKeyOf(v)) === isStable),
      }))
      .filter(cat => cat.variants.length > 0);
  }, [activeSupportTab]);

  // 确保当前选中的二级 Tab 在过滤后的列表中有效
  const effectiveCategory = filteredCategories.find(c => c.id === activeCategory)
    ? activeCategory
    : filteredCategories[0]?.id ?? '';

  const currentVariants = useMemo(() => {
    const cat = filteredCategories.find(c => c.id === effectiveCategory);
    if (!cat) return [];
    if (!search) return cat.variants;
    return cat.variants.filter(v =>
      v.label.includes(search) ||
      v.promptKey.includes(search) ||
      v.description.includes(search)
    );
  }, [effectiveCategory, filteredCategories, search]);

  if (!open) return null;

  const currentCat = filteredCategories.find(c => c.id === effectiveCategory);
  const totalCount = filteredCategories.reduce((sum, c) => sum + c.variants.length, 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="glass-card flex h-[75vh] max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* 头部 */}
        <div className="flex items-center justify-between border-b border-border/30 px-5 py-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-foreground">玩法清单</span>
            <span className="text-[11px] text-muted-foreground">{totalCount} 种变体</span>
          </div>
          <button onClick={onClose} className="icon-btn rounded p-1"><X size={16} /></button>
        </div>

        {/* 一级 Tab：已支持 / 实验中 */}
        <div className="flex border-b border-border/30">
          <button
            onClick={() => { setActiveSupportTab('supported'); setSearch(''); setActiveCategory(''); }}
            className={cn(
              'flex items-center gap-1.5 px-4 py-2 text-xs font-medium transition-colors relative',
              activeSupportTab === 'supported' ? 'text-neon-green' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <CheckCircle2 size={13} />
            已支持
            {activeSupportTab === 'supported' && (
              <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-neon-green rounded-full" />
            )}
          </button>
          <button
            onClick={() => { setActiveSupportTab('experimental'); setSearch(''); setActiveCategory(''); }}
            className={cn(
              'flex items-center gap-1.5 px-4 py-2 text-xs font-medium transition-colors relative',
              activeSupportTab === 'experimental' ? 'text-neon-green' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <FlaskConical size={13} />
            实验中
            {activeSupportTab === 'experimental' && (
              <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-neon-green rounded-full" />
            )}
          </button>
        </div>

        {/* 二级 Tab：玩法大类（自动换行，无图标） */}
        <div className="flex flex-wrap gap-x-1 gap-y-0 border-b border-border/30 px-4 py-1.5">
          {filteredCategories.map(cat => (
            <button
              key={cat.id}
              onClick={() => { setActiveCategory(cat.id); setSearch(''); }}
              className={cn(
                'flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                effectiveCategory === cat.id
                  ? 'bg-neon-green/15 text-neon-green'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/30',
              )}
            >
              <span>{cat.label}</span>
              <span className="text-[9px] opacity-60">({cat.variants.length})</span>
            </button>
          ))}
        </div>

        {/* 分类说明（如红包 tab 区分本体与特效叠加） */}
        {currentCat?.description && (
          <div className="border-b border-border/30 px-5 py-1.5 text-[11px] leading-relaxed text-muted-foreground/70">
            {currentCat.description}
          </div>
        )}

        {/* 搜索栏 */}
        <div className="flex items-center gap-2 border-b border-border/30 px-5 py-2">
          <div className="relative flex-1">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              className="input-neon w-full py-1.5 pl-8 pr-3 text-xs"
              placeholder={`搜索${currentCat?.label ?? ''}玩法变体...`}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* 变体卡片网格 */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="grid grid-cols-2 auto-rows-fr gap-2">
            {currentVariants.length === 0 ? (
              <div className="col-span-2 flex items-center justify-center py-20 text-xs text-muted-foreground">
                未找到匹配的玩法变体
              </div>
            ) : (
              currentVariants.map((variant: GameplayVariant) => {
                const detail = GAMEPLAY_DETAILS[variant.promptKey];
                const isFull = variant.supportLevel === 'full';
                const isCocos = variant.engineBadge === 'cocos';
                const cfg = isCocos
                  ? { icon: Cpu, color: 'text-orange-400', label: 'Cocos' }
                  : isFull
                    ? { icon: CheckCircle2, color: 'text-neon-green', label: '模板' }
                    : { icon: FlaskConical, color: 'text-blue-400', label: '蓝图' };
                return (
                  <div key={variant.id} className="relative h-full">
                    <Tooltip
                      wrapperClassName="flex h-full w-full"
                      content={
                        <div className="space-y-1">
                          {isCocos && (
                            <div><span className="text-muted-foreground">生成引擎</span>：Cocos Creator 3.8.8（引擎原生生成，独立于模板/蓝图）</div>
                          )}
                          {detail ? renderGameplayDetail(detail) : variant.description}
                        </div>
                      }
                    >
                      <button
                        onClick={() => { onSelect?.(variant.promptKey, variant.label, variant); onClose(); }}
                        className={cn(
                          'glass-card flex h-full w-full gap-3 p-3 text-left transition-all',
                          'cursor-pointer hover:border-primary/30 card-lift',
                        )}
                      >
                        <span className="mt-0.5 shrink-0 text-lg">{variant.icon}</span>
                        <div className="flex min-w-0 flex-1 flex-col">
                          <div className="flex min-w-0 items-center gap-1.5">
                            <span className="truncate text-sm font-medium text-foreground">{variant.label}</span>
                            <span className={cn('shrink-0 rounded px-1.5 py-0.5 text-[9px] font-medium', cfg.color, 'bg-muted/30')}>
                              {cfg.label}
                            </span>
                          </div>
                          <p className="mt-1 line-clamp-2 min-h-[2rem] text-xs leading-4 text-muted-foreground">{variant.description}</p>
                        </div>
                      </button>
                    </Tooltip>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
